import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Grid, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { toast } from "react-toastify";
import api from "../../api/client";
import { PageHeader, SectionCard, StatCard, StatusChip, EmptyState } from "../ui";
import { useAuth } from "../../context/AuthContext";
import { extractErrorMessage } from "../../utils/errorMessage";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—";

export default function AttendanceRegularizationNew() {
  const { user, hasRole } = useAuth();
  const role = user?.role || "";

  const [attendanceDate, setAttendanceDate] = useState("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [remarks, setRemarks] = useState({});
  const [actingId, setActingId] = useState("");

  const endpointForRole = useMemo(() => {
    if (hasRole("admin")) return "/api/attendance-regularization/admin-requests";
    if (hasRole("manager")) return "/api/attendance-regularization/manager-requests";
    return "/api/attendance-regularization/my-requests";
  }, [role]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(endpointForRole);
      setRequests(res.data?.requests || []);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [endpointForRole]);

  const stats = useMemo(() => {
    const pending = requests.filter((r) => r.status === "pending").length;
    const approved = requests.filter((r) => r.status === "approved").length;
    const rejected = requests.filter((r) => r.status === "rejected").length;
    return { total: requests.length, pending, approved, rejected };
  }, [requests]);

  const submit = async (e) => {
    e.preventDefault();
    if (!checkIn && !checkOut) return toast.error("Provide at least one time");
    setSubmitting(true);
    try {
      await api.post("/api/attendance-regularization/request", {
        attendanceDate, requestedCheckInTime: checkIn || undefined,
        requestedCheckOutTime: checkOut || undefined, reason,
      });
      toast.success("Request submitted");
      setAttendanceDate(""); setCheckIn(""); setCheckOut(""); setReason("");
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setSubmitting(false); }
  };

  const review = async (id, action) => {
    setActingId(id);
    try {
      const path = hasRole("admin")
        ? `/api/attendance-regularization/admin-requests/${id}/review`
        : `/api/attendance-regularization/manager-requests/${id}/review`;
      const body = hasRole("admin")
        ? { action, adminRemark: remarks[id] || "" }
        : { action, managerRemark: remarks[id] || "" };
      await api.put(path, body);
      toast.success(`Request ${action === "reject" ? "rejected" : "approved"}`);
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setActingId(""); }
  };

  const canReview = hasRole("admin", "manager");

  return (
    <Box p={3}>
      <PageHeader
        title="Attendance Regularization"
        subtitle={canReview ? "Review attendance correction requests" : "Request attendance corrections"}
        icon={<EditCalendarOutlinedIcon />}
      />

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}><StatCard label="Total" value={stats.total} accent="#6C50BF" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Pending" value={stats.pending} icon={<HourglassEmptyOutlinedIcon />} accent="#F9BC06" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Approved" value={stats.approved} icon={<CheckCircleOutlineIcon />} accent="#39C645" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Rejected" value={stats.rejected} icon={<CancelOutlinedIcon />} accent="#FF6B6B" /></Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={canReview ? 12 : 5}>
          {!canReview && (
            <SectionCard title="New Request" subtitle="Correct missed punches or wrong times" accent="#6C50BF">
              <Box component="form" onSubmit={submit}>
                <Stack spacing={2}>
                  <TextField fullWidth size="small" type="date" label="Attendance Date" InputLabelProps={{ shrink: true }} value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} required inputProps={{ max: new Date().toISOString().slice(0, 10) }} />
                  <Stack direction="row" spacing={1}>
                    <TextField fullWidth size="small" type="time" label="Check-In Time" InputLabelProps={{ shrink: true }} value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
                    <TextField fullWidth size="small" type="time" label="Check-Out Time" InputLabelProps={{ shrink: true }} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
                  </Stack>
                  <TextField fullWidth size="small" multiline minRows={3} label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explain why the correction is needed" required />
                  <Button type="submit" variant="contained" startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <SendOutlinedIcon />} disabled={submitting || !attendanceDate || !reason}>
                    {submitting ? "Submitting…" : "Submit Request"}
                  </Button>
                </Stack>
              </Box>
            </SectionCard>
          )}
        </Grid>

        <Grid item xs={12} md={canReview ? 12 : 7}>
          <SectionCard
            title={canReview ? "Requests to Review" : "My Requests"}
            subtitle={`${requests.length} items`}
            accent="#39C645"
          >
            {loading ? (
              <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
            ) : requests.length === 0 ? (
              <EmptyState title="No requests" subtitle={canReview ? "No pending regularization requests." : "You haven't submitted any requests yet."} />
            ) : (
              <Stack spacing={1.5}>
                {requests.map((r) => {
                  const isPending = r.status === "pending";
                  return (
                    <Box key={r._id} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
                      <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }}>
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap" useFlexGap>
                            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{fmtDate(r.attendanceDate)}</Typography>
                            <StatusChip status={r.status} />
                            {canReview && (r.employeeName || r.employee?.name) && (
                              <Chip label={`${r.employee?.name || r.employeeName}${r.employee?.employeeid ? ` (${r.employee.employeeid})` : r.employeeId ? ` (${r.employeeId})` : ""}`} size="small" />
                            )}
                          </Stack>
                          <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
                            In: {fmtTime(r.requestedCheckInTime)} • Out: {fmtTime(r.requestedCheckOutTime)}
                          </Typography>
                          <Typography sx={{ fontSize: 13 }}>{r.reason}</Typography>
                          {(r.managerRemark || r.adminRemark) && (
                            <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5, fontStyle: "italic" }}>
                              Remark: {r.managerRemark || r.adminRemark}
                            </Typography>
                          )}
                        </Box>
                        {canReview && isPending && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <TextField size="small" placeholder="Remark (optional)" value={remarks[r._id] || ""} onChange={(e) => setRemarks((rs) => ({ ...rs, [r._id]: e.target.value }))} sx={{ width: 180 }} />
                            <Button
                              size="small" variant="contained" color="success"
                              disabled={actingId === r._id}
                              onClick={() => review(r._id, "approve")}
                            >Approve</Button>
                            <Button
                              size="small" variant="outlined" color="error"
                              disabled={actingId === r._id}
                              onClick={() => review(r._id, "reject")}
                            >Reject</Button>
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
